/**
 * Utility function to get selected options from variant selectors
 * @param {HTMLElement} container - The container element with selects/radios
 * @returns {Array} Array of selected option values
 */
function getSelectedOptionsFromContainer(container) {
  const options = [];
  const selects = container.querySelectorAll('select');
  const radios = container.querySelectorAll('input[type="radio"]:checked');

  if (selects.length > 0) {
    selects.forEach(select => {
      options.push(select.value);
    });
  } else if (radios.length > 0) {
    radios.forEach(radio => {
      options.push(radio.value);
    });
  }

  return options;
}

if (!customElements.get('product-info')) {
  class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
    }

    connectedCallback() {
      this.variantSelects = this.querySelector('variant-selects');
      if (this.variantSelects) {
        this.initVariantSelects();
        this.applyVariantFromHash();
      }
    }

    initVariantSelects() {
      this.variantSelects.addEventListener('change', this.onVariantChange.bind(this));
    }

    applyVariantFromHash() {
      const match = window.location.hash.match(/variant=(\d+)/);
      if (!match) return;

      const variantData = this.variantSelects.querySelector('[data-variants]');
      if (!variantData) return;

      const variant = JSON.parse(variantData.textContent).find(v => String(v.id) === match[1]);
      if (!variant) return;

      variant.options.forEach((value, index) => {
        const position = index + 1;
        const select = this.variantSelects.querySelector(`[data-option-position="${position}"] select`);
        if (select) {
          select.value = value;
          return;
        }
        const radio = this.variantSelects.querySelector(
          `[data-option-position="${position}"] input[type="radio"][value="${CSS.escape(value)}"]`
        );
        if (radio) radio.checked = true;
      });

      this.variantSelects.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onVariantChange(event) {
      this.updateURL();
      this.updateVariantInput();
      this.updatePriceDisplay();
      this.updateAddToCartButton();
      this.updateSku();

      const variant = this.getCurrentVariant();
      theme.pubsub?.publish(theme.pubsub.events.VARIANT_CHANGE, {
        variant: variant,
        selectedOptions: variant ? variant.options : this.getSelectedOptions(),
        source: 'product-info'
      });
    }

    updateURL() {
      const variant = this.getCurrentVariant();
      if (!variant) return;

      const url = new URL(window.location.href);
      url.searchParams.delete('variant');
      url.hash = `variant=${variant.id}`;
      window.history.replaceState({}, '', url.toString());
    }

    updateVariantInput() {
      const variant = this.getCurrentVariant();
      if (!variant) return;

      const variantInput = this.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variant.id;
      }
    }

    updatePriceDisplay() {
      const variant = this.getCurrentVariant();
      if (!variant) return;

      const priceElement = this.querySelector('.price__amount');
      const compareElement = this.querySelector('.price__amount--compare');
      const priceWrapper = this.querySelector('.price');

      if (priceElement) {
        priceElement.textContent = this.formatMoney(variant.price);
      }

      if (priceWrapper) {
        priceWrapper.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        priceWrapper.classList.toggle('price--sold-out', !variant.available);
      }

      if (compareElement) {
        if (variant.compare_at_price > variant.price) {
          compareElement.textContent = this.formatMoney(variant.compare_at_price);
          compareElement.parentElement.hidden = false;
        } else {
          compareElement.parentElement.hidden = true;
        }
      }
    }

    updateSku() {
      const variant = this.getCurrentVariant();
      const skuValue = this.querySelector('[data-sku]');
      if (!skuValue) return;

      skuValue.textContent = variant ? variant.sku : '';
    }

    updateAddToCartButton() {
      const variant = this.getCurrentVariant();
      const button = this.querySelector('.main-product__submit');
      const buttonText = this.querySelector('.main-product__submit-text');

      if (!button || !buttonText) return;

      if (!variant) {
        button.disabled = true;
        buttonText.textContent = theme.locales?.product?.unavailable || 'Unavailable';
        return;
      }

      if (variant.available) {
        button.disabled = false;
        buttonText.textContent = theme.locales?.product?.addToCart || 'Add to cart';
      } else {
        button.disabled = true;
        buttonText.textContent = theme.locales?.product?.soldOut || 'Sold out';
      }
    }

    getCurrentVariant() {
      if (!this.variantSelects) return null;

      const variantData = this.variantSelects.querySelector('[data-variants]');
      if (!variantData) return null;

      const variants = JSON.parse(variantData.textContent);
      const selectedOptions = this.getSelectedOptions();

      return variants.find(variant => {
        return variant.options.every((option, index) => option === selectedOptions[index]);
      });
    }

    getSelectedOptions() {
      return getSelectedOptionsFromContainer(this.variantSelects);
    }

    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, theme.moneyFormat);
      }

      const amount = (cents / 100).toFixed(2);
      return `${amount} ${theme.currencyCode || ''}`.trim();
    }
  }

  customElements.define('product-info', ProductInfo);
}

if (!customElements.get('variant-selects')) {
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.pickerStyle = this.dataset.style || 'dropdown';
    }

    connectedCallback() {
      this.addEventListener('change', this.onSelectChange.bind(this));
    }

    onSelectChange(event) {
      const target = event.target;

      // Ignore custom events to prevent infinite loop
      if (event.target === this) return;

      if (this.pickerStyle === 'buttons' && target.type === 'radio') {
        this.updateButtonSelection(target);
      }

      this.updateVariantStatuses();
    }

    updateButtonSelection(selectedInput) {
      const optionGroup = selectedInput.closest('.main-product__option');
      if (!optionGroup) return;

      const allLabels = optionGroup.querySelectorAll('.main-product__option-value');
      allLabels.forEach(label => {
        label.classList.remove('main-product__option-value--selected');
      });

      const selectedLabel = selectedInput.closest('.main-product__option-value');
      if (selectedLabel) {
        selectedLabel.classList.add('main-product__option-value--selected');
      }
    }

    updateVariantStatuses() {
      const variantData = this.querySelector('[data-variants]');
      if (!variantData) return;

      const variants = JSON.parse(variantData.textContent);
      const selectedOptions = this.getSelectedOptions();

      if (this.pickerStyle === 'buttons') {
        this.updateButtonStates(variants, selectedOptions);
      }
    }

    updateButtonStates(variants, selectedOptions) {
      const variantInput = this.closest('product-info')?.querySelector('input[name="id"]')
        || document.querySelector('input[name="id"]');
      const currentVariantId = variantInput ? parseInt(variantInput.value, 10) : null;
      const currentVariant = currentVariantId ? variants.find(v => v.id === currentVariantId) : null;

      const optionGroups = this.querySelectorAll('.main-product__option[data-option-position]');

      optionGroups.forEach((group) => {
        const optionPosition = parseInt(group.dataset.optionPosition, 10) - 1;
        const buttons = group.querySelectorAll('.main-product__option-value');

        buttons.forEach(button => {
          const input = button.querySelector('input[type="radio"]');
          if (!input) return;

          const baseOptions = currentVariant ? [...currentVariant.options] : [...selectedOptions];
          baseOptions[optionPosition] = input.value;

          const matchingVariant = variants.find(variant =>
            variant.options.every((option, index) => option === baseOptions[index])
          );

          const isAvailable = matchingVariant && matchingVariant.available;
          button.classList.toggle('main-product__option-value--disabled', !isAvailable);
          input.disabled = !matchingVariant;
        });
      });
    }

    getSelectedOptions() {
      return getSelectedOptionsFromContainer(this);
    }
  }

  customElements.define('variant-selects', VariantSelects);
}

if (!customElements.get('quantity-input')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="number"]');
      this.minusButton = this.querySelector('[name="minus"]');
      this.plusButton = this.querySelector('[name="plus"]');
    }

    connectedCallback() {
      if (this.minusButton) {
        this.minusButton.addEventListener('click', this.decrease.bind(this));
      }
      if (this.plusButton) {
        this.plusButton.addEventListener('click', this.increase.bind(this));
      }
      if (this.input) {
        this.input.addEventListener('change', this.validateInput.bind(this));
      }
    }

    decrease() {
      if (!this.input) return;

      const currentValue = parseInt(this.input.value, 10) || 1;
      const min = parseInt(this.input.min, 10) || 1;

      if (currentValue > min) {
        this.input.value = currentValue - 1;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    increase() {
      if (!this.input) return;

      const currentValue = parseInt(this.input.value, 10) || 1;
      const max = parseInt(this.input.max, 10) || Infinity;

      if (currentValue < max) {
        this.input.value = currentValue + 1;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    validateInput() {
      if (!this.input) return;

      let value = parseInt(this.input.value, 10);
      const min = parseInt(this.input.min, 10) || 1;
      const max = parseInt(this.input.max, 10) || Infinity;

      if (isNaN(value) || value < min) {
        value = min;
      } else if (value > max) {
        value = max;
      }

      this.input.value = value;
    }
  }

  customElements.define('quantity-input', QuantityInput);
}

if (!customElements.get('share-button')) {
  class ShareButton extends HTMLElement {
    constructor() {
      super();
      this.url = this.dataset.url || window.location.href;
      this.trigger = this.querySelector('.main-product__share-trigger');
      this.options = this.querySelector('.main-product__share-options');
      this.copyButton = this.querySelector('[data-copy-url]');
      this.instagramButton = this.querySelector('[data-share-instagram]');

      // Bind event handlers to preserve reference for removeEventListener
      this.boundToggleOptions = this.toggleOptions.bind(this);
      this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
      this.boundCopyUrl = this.copyUrl.bind(this);
      this.boundShareInstagram = this.shareInstagram.bind(this);
    }

    connectedCallback() {
      if (this.trigger && this.options) {
        this.trigger.addEventListener('click', this.boundToggleOptions);
        document.addEventListener('click', this.boundHandleOutsideClick);
      }

      if (this.copyButton) {
        this.copyButton.addEventListener('click', this.boundCopyUrl);
      }

      if (this.instagramButton) {
        this.instagramButton.addEventListener('click', this.boundShareInstagram);
      }
    }

    disconnectedCallback() {
      if (this.trigger) {
        this.trigger.removeEventListener('click', this.boundToggleOptions);
      }
      document.removeEventListener('click', this.boundHandleOutsideClick);
      if (this.copyButton) {
        this.copyButton.removeEventListener('click', this.boundCopyUrl);
      }
      if (this.instagramButton) {
        this.instagramButton.removeEventListener('click', this.boundShareInstagram);
      }
    }

    toggleOptions(event) {
      event.stopPropagation();

      if (navigator.share && this.canUseNativeShare()) {
        this.nativeShare();
      } else {
        this.options.hidden = !this.options.hidden;
      }
    }

    canUseNativeShare() {
      return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    }

    async nativeShare() {
      try {
        await navigator.share({
          url: this.url,
          title: document.title
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.options.hidden = false;
        }
      }
    }

    handleOutsideClick(event) {
      if (!this.contains(event.target)) {
        this.options.hidden = true;
      }
    }

    async copyUrl() {
      const url = this.copyButton.dataset.copyUrl || this.url;
      try {
        await navigator.clipboard.writeText(url);
        this.copyButton.classList.add('copied');
        this.showCopyToast();
        setTimeout(() => {
          this.copyButton.classList.remove('copied');
        }, 2000);
      } catch (error) {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.showCopyToast();
      }
    }

    async shareInstagram() {
      const url = this.url;
      try {
        await navigator.clipboard.writeText(url);
      } catch (error) {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      const hint = window.theme?.locales?.instagram_share_hint || 'Link copied. Paste it in your Instagram story.';
      this.showCopyToast(hint);

      if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        window.location.href = 'instagram://story-camera';
        setTimeout(() => {
          window.open('https://www.instagram.com/', '_blank', 'noopener');
        }, 600);
      }
    }

    showCopyToast(message) {
      let toast = document.querySelector('.share-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'share-toast';
        document.body.appendChild(toast);
      }
      toast.textContent = message || window.theme?.locales?.copy_link_success || 'Link copied to clipboard';
      toast.classList.remove('share-toast--hidden');
      toast.classList.add('share-toast--visible');
      clearTimeout(this._toastTimeout);
      this._toastTimeout = setTimeout(() => {
        toast.classList.remove('share-toast--visible');
        toast.classList.add('share-toast--hidden');
      }, 2500);
    }
  }

  customElements.define('share-button', ShareButton);
}

(function initStickyGallery() {
  var media = document.querySelector('.main-product__media');
  var info = document.querySelector('.main-product__info');
  if (!media || !info) return;

  function getHeaderHeight() {
    var header = document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
    if (!header) return 0;
    if (header.classList.contains('header--hidden')) return 0;
    if (header.classList.contains('header--transparent')) return 0;
    return header.offsetHeight;
  }

  function update() {
    var headerH = getHeaderHeight();
    document.documentElement.style.setProperty('--header-height', headerH + 'px');
    var realHeader = document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
    var announcementBar = document.querySelector('announcement-bar');
    var announcementH = announcementBar ? announcementBar.offsetHeight : 0;
    document.documentElement.style.setProperty('--header-real-offset', ((realHeader ? realHeader.offsetHeight : 0) + announcementH) + 'px');

    if (window.innerWidth < 990) {
      media.classList.remove('is-sticky');
      return;
    }

    var galleryH = window.innerHeight - headerH;
    if (info.scrollHeight > galleryH) {
      media.classList.add('is-sticky');
    } else {
      media.classList.remove('is-sticky');
    }
  }

  info.addEventListener('click', function() { requestAnimationFrame(update); });

  var ro = new ResizeObserver(update);
  ro.observe(info);
  window.addEventListener('resize', update);

  var headerEl = document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
  if (headerEl) {
    var headerObserver = new MutationObserver(update);
    headerObserver.observe(headerEl, { attributes: true, attributeFilter: ['class'] });
  }

  update();
})();
